/**
 * API Service - Handles all backend communication
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Token management
export const getToken = () => localStorage.getItem('upim_token');
export const setToken = (token) => localStorage.setItem('upim_token', token);
export const removeToken = () => localStorage.removeItem('upim_token');

// Generic fetch wrapper with auth
const fetchAPI = async (endpoint, options = {}) => {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error de conexión' }));
    throw new Error(error.detail || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Auth API
export const authAPI = {
  register: (data) => fetchAPI('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  login: (email, password) => fetchAPI('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }),
  
  getProfile: () => fetchAPI('/auth/me'),
  
  updateProfile: (data) => fetchAPI('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Payment API
export const paymentAPI = {
  getPackages: () => fetchAPI('/payments/packages'),
  
  createCheckout: (packageId, originUrl) => fetchAPI('/payments/checkout', {
    method: 'POST',
    body: JSON.stringify({ package_id: packageId, origin_url: originUrl }),
  }),
  
  checkStatus: (sessionId) => fetchAPI(`/payments/status/${sessionId}`),
  
  getHistory: () => fetchAPI('/payments/history'),
};

// Public API (no auth required)
export const publicAPI = {
  getCarousels: (type) => fetchAPI(`/public/carousels${type ? `?carousel_type=${type}` : ''}`),
  
  getEvents: (upcomingOnly = true) => fetchAPI(`/public/events?upcoming_only=${upcomingOnly}`),
  
  getProviders: (category) => fetchAPI(`/public/providers${category ? `?category=${category}` : ''}`),
  
  getProviderCategories: () => fetchAPI('/public/providers/categories'),
};

// Admin API
export const adminAPI = {
  getStats: () => fetchAPI('/admin/stats'),
  
  getAffiliates: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.offset) searchParams.append('offset', params.offset);
    return fetchAPI(`/admin/affiliates?${searchParams.toString()}`);
  },
  
  getAffiliateDetail: (userId) => fetchAPI(`/admin/affiliates/${userId}`),
  
  getCarousels: (type) => fetchAPI(`/admin/carousels${type ? `?carousel_type=${type}` : ''}`),
  
  createCarouselLogo: (data) => fetchAPI('/admin/carousels', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateCarouselLogo: (logoId, data) => fetchAPI(`/admin/carousels/${logoId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  deleteCarouselLogo: (logoId) => fetchAPI(`/admin/carousels/${logoId}`, {
    method: 'DELETE',
  }),
  
  getBroadcasts: () => fetchAPI('/admin/broadcasts'),
  
  sendBroadcast: (data) => fetchAPI('/admin/broadcasts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  getProviders: (verifiedOnly = false) => fetchAPI(`/admin/providers?verified_only=${verifiedOnly}`),
};

// Axios-like API wrapper for compatibility
const api = {
  get: async (endpoint) => {
    const data = await fetchAPI(endpoint);
    return { data };
  },
  post: async (endpoint, body) => {
    const data = await fetchAPI(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { data };
  },
  put: async (endpoint, body) => {
    const data = await fetchAPI(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return { data };
  },
  delete: async (endpoint) => {
    const data = await fetchAPI(endpoint, {
      method: 'DELETE',
    });
    return { data };
  },
  auth: authAPI,
  payment: paymentAPI,
  public: publicAPI,
  admin: adminAPI,
};

export default api;
